import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';
import { AsyncPipe } from '@angular/common';
import { uiKitScope } from '../../constants/ui-constant';
import { provideUiKitScope } from '../../providers';

@Component({
  selector: 'ui-validation-form',
  imports: [TranslocoDirective, AsyncPipe],
  templateUrl: './validation-form.component.html',
  styleUrl: './validation-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideUiKitScope()],
})
export class ValidationFormComponent implements OnChanges, OnDestroy {
  control = input.required<AbstractControl>();
  showImmediately = input<boolean>(false);
  statusChangesSub$: Subscription = new Subscription();
  errorMessageKey = '';
  errorMessageParams: { [key: string]: string } = {};
  statusChanges$!: Observable<boolean>;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['control']) {
      this.statusChanges$ = this.control().statusChanges.pipe(
        startWith(this.control().status),
        map(() => this.updateErrorMessage()),
      );
      this.statusChangesSub$ = this.statusChanges$.subscribe();
    }
  }

  ngOnDestroy() {
    this.statusChangesSub$.unsubscribe();
  }

  updateErrorMessage(): boolean {
    const errors = this.control().errors;

    if (errors) {
      const errorKey = Object.keys(errors)[0];

      switch (errorKey) {
        case 'required':
          this.errorMessageKey = 'validation.required';
          break;
        case 'email':
          this.errorMessageKey = 'validation.email';
          break;
        case 'minlength':
          this.errorMessageKey = 'validation.minLength';
          this.errorMessageParams = {
            length: errors['minlength'].requiredLength,
          };
          break;
        case 'maxlength':
          this.errorMessageKey = 'validation.maxLength';
          this.errorMessageParams = {
            length: errors['maxlength'].requiredLength,
          };
          break;
        case 'passwordMismatch':
          this.errorMessageKey = 'validation.password-match';
          break;
        case 'invalidDate':
          this.errorMessageKey = 'validation.invalidDate';
          break;
        case 'invalidUrl':
          this.errorMessageKey = 'validation.invalidUrl';
          break;
        case 'invalidCodeFormat':
          this.errorMessageKey = 'validation.invalidCodeFormat';
          break;
        case 'pastDate':
          this.errorMessageKey = 'validation.pastDate';
          break;
        case 'passwordUpperCase':
          this.errorMessageKey = 'validation.passwordUpperCase';
          break;
        case 'passwordLowerCase':
          this.errorMessageKey = 'validation.passwordLowerCase';
          break;
        case 'passwordNumeric':
          this.errorMessageKey = 'validation.passwordNumeric';
          break;
        case 'passwordSpecialChar':
          this.errorMessageKey = 'validation.passwordSpecialChar';
          break;
        case 'max':
          this.errorMessageKey = 'validation.max';
          this.errorMessageParams = {
            max: errors['max'].max,
          };
          break;
        case 'min':
          this.errorMessageKey = 'validation.min';
          this.errorMessageParams = {
            min: errors['min'].min,
          };
          break;
        case 'duplicateLicenseId':
          this.errorMessageKey = 'validation.duplicateLicenseId';
          break;
        default:
          this.errorMessageKey = '';
          this.errorMessageParams = {};
      }
    } else {
      this.errorMessageKey = '';
      this.errorMessageParams = {};
    }
    return (
      this.control().invalid && (this.control().dirty || this.control().touched)
    );
  }

  protected readonly uiKitScope = uiKitScope;
}
